import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import { EMPTY_TASK_LIST_PLACEHOLDER } from '../emptyTaskListPlaceholder';
import type { FromPMHandler, FromPMHandlers } from '../types';
import { convertInlineChildren } from '../utils';
import { blockquote } from './blockquote';
import { hardBreak } from './break';
import { blockCard, inlineCard } from './card';
import { codeBlock } from './code';
import { date } from './date';
import { emoji } from './emoji';
import { heading } from './heading';
import { image } from './image';
import { convertList } from './list';
import { convertTaskItem } from './listItem';
import { createMediaHandlers, media, mediaSingle, type SerializeFileMedia } from './media';
import { mention } from './mention';
import { panel } from './panel';
import { paragraph } from './paragraph';
import { status } from './status';
import { table } from './table';
import { tableCell, tableCellContent } from './tableCell';
import { tableRow } from './tableRow';
import { text } from './text';
import { thematicBreak } from './thematicBreak';

export type SerializeMention = (node: PMNode) => string | undefined;

/** Build a mention handler with an optional identity URL builder. */
export const createMentionHandler =
	(serializeMention?: SerializeMention): FromPMHandler =>
	(node: PMNode) =>
		mention(node, serializeMention);

/**
 * Build a handler map whose recursive handlers convert children through the
 * finished map, so overrides (rich nodes, file media) also apply to nested content.
 */
export const createFromPMHandlers = (
	createOverrides: (handlers: FromPMHandlers) => FromPMHandlers = () => ({}),
): FromPMHandlers => {
	const handlers: FromPMHandlers = {
		paragraph: (node: PMNode) => paragraph(node, handlers),
		heading: (node: PMNode) => heading(node, handlers),

		text: (node: PMNode) => text(node),
		hard_break: (_node: PMNode) => hardBreak(),
		hardBreak: (_node: PMNode) => hardBreak(),
		horizontal_rule: (_node: PMNode) => thematicBreak(),
		rule: (_node: PMNode) => thematicBreak(),

		image: (node: PMNode) => image(node),
		inlineCard: (node: PMNode) => inlineCard(node),

		// `media` and `mediaSingle` map to a CommonMark image. The default
		// handler only knows how to serialise `type: "external"` media (which
		// has a public URL); `type: "file"` media is dropped because we don't
		// know the tenant or content scope here. Use `createMediaHandlers({
		// serializeFileMedia })` from './handlers/media' to opt in to
		// proxy-URL serialisation for file media (e.g. from Confluence).
		media: (node: PMNode) => media(node),
		mediaSingle: (node: PMNode) => mediaSingle(node),
		media_single: (node: PMNode) => mediaSingle(node),

		code_block: (node: PMNode) => codeBlock(node),
		codeBlock: (node: PMNode) => codeBlock(node),

		blockquote: (node: PMNode) => blockquote(node, handlers),
		block_quote: (node: PMNode) => blockquote(node, handlers),

		table: (node: PMNode) => table(node, handlers),
		tableRow: (node: PMNode) => tableRow(node, handlers),
		tableCell: (node: PMNode) => tableCell(node, handlers),
		tableHeader: (node: PMNode) => tableCell(node, handlers),

		bullet_list: (node: PMNode) => convertList(node, false, handlers),
		bulletList: (node: PMNode) => convertList(node, false, handlers),
		ordered_list: (node: PMNode) => convertList(node, true, handlers),
		orderedList: (node: PMNode) => convertList(node, true, handlers),
		task_list: (node: PMNode) => convertList(node, false, handlers),
		taskList: (node: PMNode) => convertList(node, false, handlers),

		list_item: (node: PMNode) => convertTaskItem(node, handlers),
		listItem: (node: PMNode) => convertTaskItem(node, handlers),
		// task_item (snake_case ADF) also uses convertTaskItem so checked/state attrs are read
		task_item: (node: PMNode) => convertTaskItem(node, handlers),

		// ADF taskItem has inline content directly (no paragraph wrapper),
		// so we wrap it in a paragraph to satisfy the listItem → block content requirement.
		taskItem: (node: PMNode) => {
			const checked =
				node.attrs.state === 'DONE'
					? true
					: node.attrs.state === 'TODO'
						? false
						: (node.attrs.checked ?? null);
			const children = convertInlineChildren(node, handlers);
			return {
				type: 'listItem' as const,
				spread: false,
				checked,
				children: [
					{
						type: 'paragraph' as const,
						children:
							children.length > 0
								? children
								: checked !== null
									? [{ type: 'text' as const, value: EMPTY_TASK_LIST_PLACEHOLDER }]
									: [],
					},
				],
			};
		},
	};
	return Object.assign(handlers, createOverrides(handlers));
};

/**
 * Default handlers for converting ProseMirror nodes to mdast.
 *
 * Handlers that need access to the full handler map receive it via
 * a closure over the map so they can recursively convert children.
 */
export const fromPMHandlers: FromPMHandlers = createFromPMHandlers();

/**
 * Lossy but readable GFM handlers for ADF nodes with no Markdown equivalent.
 * Keep this separate from the default map until the shared rollout gate is validated.
 * `serializeFileMedia` applies to file media at any depth, including inside panels and lists.
 * Callers with a whole-document fallback can opt in to rejecting unknown nodes.
 * Inline media is intentionally omitted because this serializer has no resolvable URL for it.
 */
export const createRichFromPMHandlers = ({
	serializeFileMedia,
	serializeMention,
	throwOnUnsupportedNestedNode = false,
}: {
	serializeFileMedia?: SerializeFileMedia;
	serializeMention?: SerializeMention;
	throwOnUnsupportedNestedNode?: boolean;
} = {}): FromPMHandlers =>
	createFromPMHandlers((handlers) => ({
		tableCell: (node) => ({ type: 'tableCell', children: tableCellContent(node, handlers) }),
		tableHeader: (node) => ({ type: 'tableCell', children: tableCellContent(node, handlers) }),
		mention: createMentionHandler(serializeMention),
		emoji,
		mediaInline: () => null,
		status,
		date,
		blockCard,
		embedCard: blockCard,
		panel: (node) => panel(node, handlers),
		...(serializeFileMedia ? createMediaHandlers({ serializeFileMedia }) : {}),
		...(throwOnUnsupportedNestedNode
			? {
					onUnsupportedNode: (node: PMNode) => {
						throw new Error(
							`ProseMirror node type "${node.type.name}" is not supported by the readable GFM handlers.`,
						);
					},
				}
			: {}),
	}));
